import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Toggle } from '@ui/components/Toggle/Toggle'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'
import { ShowIcon } from '@ui/icons/Show.icon'

import { useWindowResize } from '@utils/domUtils'

import {
  ClientEmailDto,
  ClientPhoneNumberDto,
  getVerifiedPhoneNumbers,
} from '@model/AccountDetailedDto'
import { NameDto } from '@model/NameDto'
import { TwoFAProviderEnum, TwoFAProviderNamesEnum } from '@model/TwoFactorAuthentication'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

import { TwoFactorAuthenticationProps } from './TwoFactorAuthentication'

interface TwoFactorAuthMethodsListProps extends TwoFactorAuthenticationProps {
  title?: string
  subtitle?: string
  availableActionTypes?: any
  hasRecoveryCodes?: boolean
  isAdvancedSettingsList?: boolean
}

interface TwoFactorAuthMethodsProviderTitle {
  provider: NameDto
  checkBoxSwitchValue: boolean
  onPhoneNumberChange: (phone: string, provider: NameDto) => void
  isPhoneDisabled: boolean
  email?: ClientEmailDto
  phone?: string
  isAdvancedSettingsList?: boolean
  verifiedPhones?: ClientPhoneNumberDto[]
}

interface TwoFactorCheckboxSwitchProps {
  areActionProvidersDisabled: boolean
  isAdvancedSettingsList?: boolean
  isPhoneDisabled: boolean
  checkBoxSwitchProps: {
    disabled: boolean
    name: string
    value: boolean
    onChange: (value: boolean) => void
  }
}

export const TwoFactorAuthProviderTitle: React.FC<TwoFactorAuthMethodsProviderTitle> = ({
  provider,
  checkBoxSwitchValue,
  onPhoneNumberChange,
  isPhoneDisabled,
  email,
  phone,
  isAdvancedSettingsList,
  verifiedPhones,
}) => {
  const isMobile = useWindowResize()
  const { t } = useTranslation()
  return (
    <Box flex align='center' gap='x4'>
      <Box
        flex
        align={isMobile ? 'start' : 'center'}
        direction={isMobile ? 'column-reverse' : 'row'}
        gap='x4'
      >
        <Body1 semibold>
          {t(`TwoFactorAuth.${provider.name}`, { defaultValue: provider.name })}
        </Body1>
        {!isAdvancedSettingsList && provider.id === TwoFAProviderEnum.GoogleAuthenticator && (
          <Badge type='green' contrast='low' size='xxs'>
            {t('Recommended')}
          </Badge>
        )}
      </Box>
      {!isAdvancedSettingsList && provider.id === TwoFAProviderEnum.SMS && (
        <Tooltip
          autoHide={2000}
          content={t(
            'Profile.SMS can be enabled together with Email to increase the deliverability of the verification code'
          )}
          direction={'bottom'}
        >
          <ActionIcon icon={InfoIcon} size='x20' />
        </Tooltip>
      )}
      {!isAdvancedSettingsList &&
        provider.id === TwoFAProviderEnum.Email &&
        checkBoxSwitchValue && (
          <Body1 color='text-base-em' medium>
            {email?.address}
          </Body1>
        )}
      {!isAdvancedSettingsList && provider.id === TwoFAProviderEnum.SMS && !isPhoneDisabled && (
        <span className='is-flex is-align-items-center'>
          {checkBoxSwitchValue && (
            <Body1 color='text-base-em' medium>
              {phone}
            </Body1>
          )}
          {checkBoxSwitchValue && verifiedPhones && verifiedPhones?.length > 1 && (
            <button
              onClick={() => phone && onPhoneNumberChange(phone, provider)}
              className={classNames('is-link', 'ml-1')}
            >
              <Body1>{t('Profile.Change')}</Body1>
            </button>
          )}
        </span>
      )}
    </Box>
  )
}

const TwoFactorCheckboxSwitch = ({
  areActionProvidersDisabled,
  isAdvancedSettingsList,
  isPhoneDisabled,
  checkBoxSwitchProps,
}: TwoFactorCheckboxSwitchProps) => {
  const isMobile = useWindowResize()
  const { t } = useTranslation()

  const infoModal = useModal(InformationModal)
  if (isMobile) {
    return (
      <span
        onClick={() => {
          if (areActionProvidersDisabled && isAdvancedSettingsList) {
            infoModal.open({ description: t('Profile.Please choose the method first') })
          } else if (isPhoneDisabled) {
            infoModal.open({
              description: t(`Profile.Verify your phone number at 'My Phone Numbers'`),
            })
          }
        }}
      >
        <Toggle {...checkBoxSwitchProps} />
      </span>
    )
  }

  const tooltipContent =
    areActionProvidersDisabled && isAdvancedSettingsList
      ? t('Profile.Please choose the method first')
      : isPhoneDisabled
      ? t(`Profile.Verify your phone number at 'My Phone Numbers'`)
      : null

  return (
    <span>
      <Tooltip
        autoHide={2000}
        direction='topLeft'
        disabled={!tooltipContent}
        content={tooltipContent || ''}
      >
        <Toggle {...checkBoxSwitchProps} />
      </Tooltip>
    </span>
  )
}

export const TwoFactorAuthMethodsList: React.FC<TwoFactorAuthMethodsListProps> = ({
  account,
  providers,
  activeProviders,
  twoFAProvidersActionTypes,
  onPhoneNumberChange,
  onProviderToggle,
  onAuthActionsProviderToggle,
  onGenerateRecoveryCodes,
  hasRecoveryCodes,
  isAdvancedSettingsList,
}) => {
  const { t } = useTranslation()

  const email = account?.emails.find((x) => x.isPrimary)
  const phone = activeProviders.find(
    (activeProvider) => activeProvider.type.id === TwoFAProviderEnum.SMS
  )?.authMedium
  const verifiedPhones = getVerifiedPhoneNumbers(account)

  return (
    <Box flex direction='column' gap='x16'>
      {[...(isAdvancedSettingsList ? twoFAProvidersActionTypes : providers)].map(
        (provider: NameDto) => {
          const areActionProvidersDisabled = activeProviders.length === 0
          const checkBoxSwitchValue = isAdvancedSettingsList
            ? activeProviders.length > 0 &&
              activeProviders[0].authActions.some((authAction) => authAction.id === provider.id)
            : activeProviders.some((activeProvider) => activeProvider.type.id === provider.id)
          const isPhoneDisabled =
            !verifiedPhones?.length &&
            provider.id === TwoFAProviderEnum.SMS &&
            provider.name === TwoFAProviderNamesEnum.SMS

          const checkBoxSwitchProps = {
            disabled: (areActionProvidersDisabled && isAdvancedSettingsList) || isPhoneDisabled,
            name: provider.name,
            value: checkBoxSwitchValue,
            onChange: (value: boolean) =>
              isAdvancedSettingsList
                ? onAuthActionsProviderToggle(provider, value)
                : onProviderToggle(provider, value),
          }

          return (
            <Box flex align='center' justify='space-between' key={provider.name}>
              <TwoFactorAuthProviderTitle
                provider={provider}
                checkBoxSwitchValue={checkBoxSwitchValue}
                onPhoneNumberChange={onPhoneNumberChange}
                isPhoneDisabled={isPhoneDisabled}
                email={email}
                phone={phone}
                isAdvancedSettingsList={isAdvancedSettingsList}
                verifiedPhones={verifiedPhones}
              />
              <TwoFactorCheckboxSwitch
                areActionProvidersDisabled={areActionProvidersDisabled}
                isAdvancedSettingsList={isAdvancedSettingsList}
                isPhoneDisabled={isPhoneDisabled}
                checkBoxSwitchProps={checkBoxSwitchProps}
              />
            </Box>
          )
        }
      )}
      {activeProviders.length > 0 && hasRecoveryCodes && (
        <Box flex align='center' gap='x4' onClick={() => onGenerateRecoveryCodes()}>
          <ShowIcon />
          <Body1 semibold decoration='underline' color='text-purple-high-em'>
            {t('Profile.View Recovery Codes')}
          </Body1>
        </Box>
      )}
    </Box>
  )
}
